import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Cloud, LayoutDashboard, Server, Globe, Shield, Mail, LogOut, Plus,
  Pencil, Trash2, Check, X, Save, Loader2, Search, BookOpen, Gift,
  Eye, EyeOff,
} from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const categories = [
  { id: "all", label: "All Plans", icon: LayoutDashboard },
  { id: "gpu", label: "GPU Cloud", icon: Server },
  { id: "vps", label: "VPS", icon: Server },
  { id: "shared", label: "Shared", icon: Globe },
  { id: "cloud", label: "Cloud", icon: Cloud },
  { id: "wordpress", label: "WordPress", icon: Globe },
  { id: "domains", label: "Domains", icon: Globe },
  { id: "ssl", label: "SSL", icon: Shield },
  { id: "email", label: "Email", icon: Mail },
];
const emptyPlan = {
  name: "", category: "shared", price: 0, price_period: "month",
  original_price: "", discount_percent: "", badge: "",
  features: [""], highlighted: false, is_active: true, specs: {},
};

const emptyBlog = {
  title: "", slug: "", excerpt: "", content: "", author: "IndiaCloud Team",
  tags: "", cover_image: "", published: false,
};

function getAuthHeader() {
  const token = localStorage.getItem("admin_token");
  return { Authorization: `Bearer ${token}` };
}

export default function AdminDashboard() {
  const [plans, setPlans] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [activeTab, setActiveTab] = useState("plans"); // plans | blog | referrals
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [editPlan, setEditPlan] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyPlan);
  const [saving, setSaving] = useState(false);
  // Blog state
  const [editBlog, setEditBlog] = useState(null);
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [blogForm, setBlogForm] = useState(emptyBlog);
  const navigate = useNavigate();

  const fetchPlans = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/plans/admin/all`, { headers: getAuthHeader() });
      setPlans(res.data);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.removeItem("admin_token"); navigate("/admin"); }
    } finally { setLoading(false); }
  }, [navigate]);

  const fetchBlogs = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/blog/admin/all`, { headers: getAuthHeader() });
      setBlogs(res.data);
    } catch {}
  }, []);

  const fetchReferrals = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/referrals/admin`, { headers: getAuthHeader() });
      setReferrals(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) { navigate("/admin"); return; }
    fetchPlans();
    fetchBlogs();
    fetchReferrals();
  }, [fetchPlans, fetchBlogs, fetchReferrals, navigate]);

  const logout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_user");
    navigate("/admin");
  };

  const filteredPlans = plans.filter((p) => {
    const catMatch = filter === "all" || p.category === filter;
    const searchMatch = !search || p.name.toLowerCase().includes(search.toLowerCase());
    return catMatch && searchMatch;
  });

  const openCreate = () => {
    setEditPlan(null);
    setForm({ ...emptyPlan, features: [""] });
    setShowForm(true);
  };

  const openEdit = (plan) => {
    setEditPlan(plan);
    setForm({
      ...plan,
      features: plan.features?.length ? plan.features : [""],
      original_price: plan.original_price || "",
      discount_percent: plan.discount_percent || "",
      badge: plan.badge || "",
    });
    setShowForm(true);
  };

  const savePlan = async () => {
    setSaving(true);
    try {
      const data = {
        ...form,
        price: parseFloat(form.price),
        original_price: form.original_price ? parseFloat(form.original_price) : null,
        discount_percent: form.discount_percent ? parseInt(form.discount_percent) : null,
        badge: form.badge || null,
        features: form.features.filter((f) => f.trim()),
      };
      if (editPlan) {
        await axios.put(`${API}/plans/${editPlan.id}`, data, { headers: getAuthHeader() });
        toast.success("Plan updated successfully!");
      } else {
        await axios.post(`${API}/plans`, data, { headers: getAuthHeader() });
        toast.success("Plan created successfully!");
      }
      setShowForm(false);
      fetchPlans();
    } catch (err) {
      toast.error("Failed to save plan. " + (err.response?.data?.detail || ""));
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = async (id, name) => {
    if (!window.confirm(`Delete "${name}"?`)) return;
    try {
      await axios.delete(`${API}/plans/${id}`, { headers: getAuthHeader() });
      toast.success("Plan deleted.");
      fetchPlans();
    } catch {
      toast.error("Failed to delete plan.");
    }
  };

  const addFeature = () => setForm((f) => ({ ...f, features: [...f.features, ""] }));
  const updateFeature = (i, v) => setForm((f) => ({ ...f, features: f.features.map((x, idx) => idx === i ? v : x) }));
  const removeFeature = (i) => setForm((f) => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));

  const stats = {
    total: plans.length,
    active: plans.filter((p) => p.is_active).length,
    popular: plans.filter((p) => p.highlighted).length,
    categories: [...new Set(plans.map((p) => p.category))].length,
  };

  return (
    <div data-testid="admin-dashboard" style={{ background: "#030305", minHeight: "100vh", display: "flex" }}>
      {/* Sidebar */}
      <aside className="admin-sidebar flex flex-col">
        <div className="p-5 border-b" style={{ borderColor: "rgba(0,243,255,0.1)" }}>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: "linear-gradient(135deg, #00F3FF, #BC13FE)" }}>
              <Cloud size={15} color="#030305" />
            </div>
            <div>
              <div className="font-orbitron font-700 text-xs text-gradient tracking-wider">INDIACLOUD</div>
              <div className="font-rajdhani text-xs text-gray-600">Admin Panel</div>
            </div>
          </div>
        </div>

        <nav className="flex-1 p-4 overflow-y-auto">
          {/* Main tabs */}
          <div className="font-rajdhani font-600 text-xs text-gray-700 tracking-widest mb-3 px-2">SECTIONS</div>
          {[
            { id: "plans", label: "Plans Manager", icon: LayoutDashboard },
            { id: "blog", label: "Blog Posts", icon: BookOpen },
            { id: "referrals", label: "Referrals", icon: Gift },
          ].map((tab) => (
            <button key={tab.id} data-testid={`tab-${tab.id}`} onClick={() => setActiveTab(tab.id)}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl mb-1 text-left transition-all duration-200"
              style={{
                background: activeTab === tab.id ? "rgba(0,243,255,0.1)" : "transparent",
                border: activeTab === tab.id ? "1px solid rgba(0,243,255,0.25)" : "1px solid transparent",
                color: activeTab === tab.id ? "#00F3FF" : "#9ca3af",
              }}>
              <tab.icon size={14} />
              <span className="font-rajdhani font-600 text-sm">{tab.label}</span>
            </button>
          ))}

          {/* Category filter — only for plans tab */}
          {activeTab === "plans" && (
            <>
              <div className="font-rajdhani font-600 text-xs text-gray-700 tracking-widest mt-5 mb-3 px-2">CATEGORIES</div>
              {categories.map((cat) => (
                <button key={cat.id} data-testid={`sidebar-${cat.id}`} onClick={() => setFilter(cat.id)}
                  className="w-full flex items-center gap-3 px-3 py-2 rounded-xl mb-0.5 text-left transition-all duration-200"
                  style={{
                    background: filter === cat.id ? "rgba(188,19,254,0.08)" : "transparent",
                    border: filter === cat.id ? "1px solid rgba(188,19,254,0.2)" : "1px solid transparent",
                    color: filter === cat.id ? "#BC13FE" : "#6b7280",
                  }}>
                  <cat.icon size={12} />
                  <span className="font-rajdhani font-600 text-xs">{cat.label}</span>
                  <span className="ml-auto text-xs rounded-full px-1.5 py-0.5"
                    style={{ background: "rgba(255,255,255,0.05)", color: "#6b7280" }}>
                    {cat.id === "all" ? plans.length : plans.filter((p) => p.category === cat.id).length}
                  </span>
                </button>
              ))}
            </>
          )}
        </nav>

        <div className="p-4 border-t" style={{ borderColor: "rgba(0,243,255,0.08)" }}>
          <button onClick={logout} data-testid="admin-logout"
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-gray-500 hover:text-white transition-all text-sm font-rajdhani font-600">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="admin-content flex-1">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-orbitron font-700 text-xl text-white">
              {activeTab === "plans" && "Plans Management"}
              {activeTab === "blog" && "Blog Posts"}
              {activeTab === "referrals" && "Referral Program"}
            </h1>
            <p className="font-outfit text-sm text-gray-500 mt-0.5">
              {activeTab === "plans" && "Manage all hosting plans and pricing"}
              {activeTab === "blog" && "Create and manage blog articles"}
              {activeTab === "referrals" && "Track referral signups and credits"}
            </p>
          </div>
          {activeTab === "plans" && (
            <button data-testid="add-plan-btn" onClick={openCreate}
              className="btn-primary flex items-center gap-2 px-5 py-2.5 text-sm font-700">
              <Plus size={16} /> ADD PLAN
            </button>
          )}
          {activeTab === "blog" && (
            <button data-testid="add-blog-btn" onClick={() => { setEditBlog(null); setBlogForm(emptyBlog); setShowBlogForm(true); }}
              className="btn-primary flex items-center gap-2 px-5 py-2.5 text-sm font-700">
              <Plus size={16} /> NEW POST
            </button>
          )}
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Total Plans", value: stats.total, color: "#00F3FF" },
            { label: "Blog Posts", value: blogs.length, color: "#BC13FE" },
            { label: "Referrals", value: referrals.length, color: "#00FF94" },
            { label: "Categories", value: stats.categories, color: "#ffcc00" },
          ].map((s) => (
            <div key={s.label} className="stat-card">
              <div className="font-orbitron font-900 text-2xl mb-1" style={{ color: s.color }}>{s.value}</div>
              <div className="font-outfit text-xs text-gray-500">{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── PLANS TAB ── */}
        {activeTab === "plans" && (
          <>
            <div className="flex items-center gap-3 mb-5">
              <div className="flex items-center gap-2 flex-1 px-3 py-2.5 rounded-xl"
                style={{ background: "rgba(10,10,18,0.8)", border: "1px solid rgba(0,243,255,0.12)" }}>
                <Search size={14} color="#00F3FF" />
                <input data-testid="plan-search" value={search} onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search plans..." className="flex-1 bg-transparent outline-none font-outfit text-sm text-gray-300 placeholder-gray-700" />
              </div>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 size={24} color="#00F3FF" className="animate-spin" />
              </div>
            ) : (
              <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(0,243,255,0.1)" }}>
                <table className="w-full">
                  <thead>
                    <tr style={{ background: "rgba(10,10,18,0.9)", borderBottom: "1px solid rgba(0,243,255,0.08)" }}>
                      {["Plan Name", "Category", "Price", "Status", "Popular", "Actions"].map((h) => (
                        <th key={h} className="text-left px-4 py-3 font-rajdhani font-700 text-xs text-gray-500 tracking-wider uppercase">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPlans.map((plan, i) => (
                      <tr key={plan.id} data-testid={`plan-row-${plan.id}`}
                        style={{ background: i % 2 === 0 ? "rgba(10,10,18,0.4)" : "rgba(10,10,18,0.2)", borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
                        <td className="px-4 py-3">
                          <div className="font-rajdhani font-700 text-sm text-white">{plan.name}</div>
                          {plan.badge && <span className="text-xs font-outfit px-1.5 py-0.5 rounded" style={{ background: "rgba(0,243,255,0.1)", color: "#00F3FF" }}>{plan.badge}</span>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="font-rajdhani font-600 text-xs px-2 py-1 rounded-full capitalize"
                            style={{ background: "rgba(188,19,254,0.1)", color: "#BC13FE", border: "1px solid rgba(188,19,254,0.2)" }}>{plan.category}</span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-orbitron font-700 text-sm text-white">₹{plan.price.toLocaleString("en-IN")}</div>
                          <div className="font-outfit text-xs text-gray-600">/{plan.price_period}</div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <div className={`w-2 h-2 rounded-full ${plan.is_active ? "bg-green-400" : "bg-red-400"}`} />
                            <span className="font-outfit text-xs text-gray-500">{plan.is_active ? "Active" : "Inactive"}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          {plan.highlighted ? <Check size={14} color="#00FF94" /> : <X size={14} color="#6b7280" />}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <button data-testid={`edit-plan-${plan.id}`} onClick={() => openEdit(plan)}
                              className="p-1.5 rounded-lg transition-all hover:scale-110"
                              style={{ background: "rgba(0,243,255,0.1)", border: "1px solid rgba(0,243,255,0.2)" }}>
                              <Pencil size={12} color="#00F3FF" />
                            </button>
                            <button data-testid={`delete-plan-${plan.id}`} onClick={() => deletePlan(plan.id, plan.name)}
                              className="p-1.5 rounded-lg transition-all hover:scale-110"
                              style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)" }}>
                              <Trash2 size={12} color="#ef4444" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredPlans.length === 0 && (
                  <div className="text-center py-10"><p className="font-outfit text-sm text-gray-600">No plans found.</p></div>
                )}
              </div>
            )}
          </>
        )}

        {/* ── BLOG TAB ── */}
        {activeTab === "blog" && (
          <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(188,19,254,0.12)" }}>
            <table className="w-full">
              <thead>
                <tr style={{ background: "rgba(10,10,18,0.9)", borderBottom: "1px solid rgba(188,19,254,0.08)" }}>
                  {["Title", "Author", "Tags", "Status", "Date", "Actions"].map((h) => (
                    <th key={h} className="text-left px-4 py-3 font-rajdhani font-700 text-xs text-gray-500 tracking-wider uppercase">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {blogs.map((post, i) => (
                  <tr key={post.id} data-testid={`blog-row-${post.id}`}
                    style={{ background: i % 2 === 0 ? "rgba(10,10,18,0.4)" : "rgba(10,10,18,0.2)", borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
                    <td className="px-4 py-3 max-w-xs">
                      <div className="font-rajdhani font-700 text-sm text-white truncate">{post.title}</div>
                      <div className="font-outfit text-xs text-gray-600 truncate">/blog/{post.slug}</div>
                    </td>
                    <td className="px-4 py-3"><span className="font-outfit text-xs text-gray-400">{post.author}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap gap-1">
                        {(post.tags || []).slice(0, 2).map(t => (
                          <span key={t} className="text-xs px-1.5 py-0.5 rounded font-outfit" style={{ background: "rgba(188,19,254,0.1)", color: "#BC13FE" }}>{t}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1.5">
                        <div className={`w-2 h-2 rounded-full ${post.published ? "bg-green-400" : "bg-yellow-400"}`} />
                        <span className="font-outfit text-xs text-gray-500">{post.published ? "Published" : "Draft"}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="font-outfit text-xs text-gray-600">
                        {new Date(post.created_at).toLocaleDateString("en-IN")}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button data-testid={`edit-blog-${post.id}`} onClick={() => {
                          setEditBlog(post);
                          setBlogForm({ ...post, tags: (post.tags || []).join(", ") });
                          setShowBlogForm(true);
                        }} className="p-1.5 rounded-lg" style={{ background: "rgba(0,243,255,0.1)", border: "1px solid rgba(0,243,255,0.2)" }}>
                          <Pencil size={12} color="#00F3FF" />
                        </button>
                        <button onClick={async () => {
                          if (!window.confirm(`Delete "${post.title}"?`)) return;
                          try {
                            await axios.delete(`${API}/blog/${post.id}`, { headers: getAuthHeader() });
                            toast.success("Post deleted"); fetchBlogs();
                          } catch { toast.error("Failed to delete post"); }
                        }} className="p-1.5 rounded-lg" style={{ background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.2)" }}>
                          <Trash2 size={12} color="#ef4444" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {blogs.length === 0 && <div className="text-center py-10"><p className="font-outfit text-sm text-gray-600">No blog posts yet.</p></div>}
          </div>
        )}

        {/* ── REFERRALS TAB ── */}
        {activeTab === "referrals" && (
          <div>
            <div className="grid grid-cols-3 gap-4 mb-6">
              {[
                { label: "Total Referrals", value: referrals.length, color: "#00FF94" },
                { label: "Total Credits Issued", value: `₹${referrals.reduce((s, r) => s + (r.credits_earned || 0), 0).toLocaleString("en-IN")}`, color: "#00F3FF" },
                { label: "Active Codes", value: referrals.filter(r => r.status === "active").length, color: "#BC13FE" },
              ].map(s => (
                <div key={s.label} className="stat-card">
                  <div className="font-orbitron font-900 text-xl mb-1" style={{ color: s.color }}>{s.value}</div>
                  <div className="font-outfit text-xs text-gray-500">{s.label}</div>
                </div>
              ))}
            </div>
            <div className="rounded-2xl overflow-hidden" style={{ border: "1px solid rgba(0,255,148,0.12)" }}>
              <table className="w-full">
                <thead>
                  <tr style={{ background: "rgba(10,10,18,0.9)", borderBottom: "1px solid rgba(0,255,148,0.08)" }}>
                    {["Email", "Referral Code", "Referred Count", "Credits Earned", "Status", "Date"].map(h => (
                      <th key={h} className="text-left px-4 py-3 font-rajdhani font-700 text-xs text-gray-500 tracking-wider uppercase">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {referrals.map((ref, i) => (
                    <tr key={ref.id} style={{ background: i % 2 === 0 ? "rgba(10,10,18,0.4)" : "rgba(10,10,18,0.2)", borderBottom: "1px solid rgba(255,255,255,0.03)" }}>
                      <td className="px-4 py-3 font-outfit text-sm text-gray-300">{ref.referrer_email}</td>
                      <td className="px-4 py-3"><span className="font-orbitron text-xs text-white px-2 py-1 rounded" style={{ background: "rgba(0,243,255,0.08)", border: "1px solid rgba(0,243,255,0.2)" }}>{ref.referral_code}</span></td>
                      <td className="px-4 py-3 font-outfit text-sm text-gray-400">{ref.referred_count || 0}</td>
                      <td className="px-4 py-3 font-orbitron text-sm" style={{ color: "#00FF94" }}>₹{(ref.credits_earned || 0).toLocaleString("en-IN")}</td>
                      <td className="px-4 py-3"><span className="font-outfit text-xs px-2 py-1 rounded-full" style={{ background: "rgba(0,255,148,0.1)", color: "#00FF94" }}>{ref.status}</span></td>
                      <td className="px-4 py-3 font-outfit text-xs text-gray-600">{new Date(ref.created_at).toLocaleDateString("en-IN")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {referrals.length === 0 && <div className="text-center py-10"><p className="font-outfit text-sm text-gray-600">No referrals yet.</p></div>}
            </div>
          </div>
        )}
      </main>

      {/* Edit/Create Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(8px)" }}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl"
            style={{ background: "#0A0A12", border: "1px solid rgba(0,243,255,0.2)", boxShadow: "0 0 40px rgba(0,243,255,0.1)" }}>
            <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "rgba(0,243,255,0.1)" }}>
              <h2 className="font-rajdhani font-700 text-base text-white">
                {editPlan ? "Edit Plan" : "Create New Plan"}
              </h2>
              <button onClick={() => setShowForm(false)} className="text-gray-500 hover:text-white transition-colors">
                <X size={18} />
              </button>
            </div>

            <div className="p-5 grid grid-cols-2 gap-4">
              {/* Name */}
              <div className="col-span-2">
                <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">PLAN NAME</label>
                <input data-testid="form-plan-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="neon-input" placeholder="e.g. IC VPS Pro" />
              </div>

              {/* Category */}
              <div>
                <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">CATEGORY</label>
                <select data-testid="form-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="neon-input">
                  {["gpu", "vps", "shared", "cloud", "domains", "ssl", "email"].map((c) => (
                    <option key={c} value={c} style={{ background: "#0A0A12" }}>{c}</option>
                  ))}
                </select>
              </div>

              {/* Price period */}
              <div>
                <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">BILLING PERIOD</label>
                <select value={form.price_period} onChange={(e) => setForm({ ...form, price_period: e.target.value })}
                  className="neon-input">
                  <option value="month" style={{ background: "#0A0A12" }}>Monthly</option>
                  <option value="year" style={{ background: "#0A0A12" }}>Yearly</option>
                </select>
              </div>

              {/* Price */}
              <div>
                <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">PRICE (₹)</label>
                <input data-testid="form-price" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="neon-input" placeholder="599" />
              </div>

              {/* Original price */}
              <div>
                <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">ORIGINAL PRICE (₹)</label>
                <input type="number" value={form.original_price} onChange={(e) => setForm({ ...form, original_price: e.target.value })} className="neon-input" placeholder="1649" />
              </div>

              {/* Discount */}
              <div>
                <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">DISCOUNT %</label>
                <input type="number" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} className="neon-input" placeholder="64" />
              </div>

              {/* Badge */}
              <div>
                <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">BADGE</label>
                <input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} className="neon-input" placeholder="MOST POPULAR" />
              </div>

              {/* Checkboxes */}
              <div className="col-span-2 flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.highlighted} onChange={(e) => setForm({ ...form, highlighted: e.target.checked })} className="w-4 h-4" />
                  <span className="font-rajdhani font-600 text-sm text-gray-400">Highlight (Popular)</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} className="w-4 h-4" />
                  <span className="font-rajdhani font-600 text-sm text-gray-400">Active</span>
                </label>
              </div>

              {/* Features */}
              <div className="col-span-2">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider">FEATURES</label>
                  <button onClick={addFeature} className="flex items-center gap-1 text-xs font-rajdhani font-700 px-2 py-1 rounded"
                    style={{ color: "#00F3FF", background: "rgba(0,243,255,0.08)", border: "1px solid rgba(0,243,255,0.2)" }}>
                    <Plus size={10} /> ADD
                  </button>
                </div>
                <div className="flex flex-col gap-2">
                  {form.features.map((feat, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <input value={feat} onChange={(e) => updateFeature(i, e.target.value)}
                        className="neon-input flex-1 text-sm" placeholder={`Feature ${i + 1}`} />
                      {form.features.length > 1 && (
                        <button onClick={() => removeFeature(i)} className="text-red-500 hover:text-red-400 flex-shrink-0">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 p-5 border-t" style={{ borderColor: "rgba(0,243,255,0.08)" }}>
              <button onClick={() => setShowForm(false)} className="btn-neon px-5 py-2 text-sm">Cancel</button>
              <button data-testid="save-plan-btn" onClick={savePlan} disabled={saving}
                className="btn-primary flex items-center gap-2 px-5 py-2 text-sm font-700">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {editPlan ? "UPDATE PLAN" : "CREATE PLAN"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Blog Edit/Create Modal */}
      {showBlogForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{ background: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)" }}>
          <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl"
            style={{ background: "#0A0A12", border: "1px solid rgba(188,19,254,0.2)" }}>
            <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "rgba(188,19,254,0.1)" }}>
              <h2 className="font-rajdhani font-700 text-base text-white">
                {editBlog ? "Edit Post" : "New Blog Post"}
              </h2>
              <button onClick={() => setShowBlogForm(false)} className="text-gray-500 hover:text-white"><X size={18} /></button>
            </div>
            <div className="p-5 flex flex-col gap-4">
              <div>
                <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">TITLE</label>
                <input data-testid="blog-form-title" value={blogForm.title} onChange={e => {
                  const title = e.target.value;
                  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
                  setBlogForm(f => ({ ...f, title, slug }));
                }} className="neon-input" placeholder="Post title" />
              </div>
              <div>
                <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">SLUG (URL)</label>
                <input value={blogForm.slug} onChange={e => setBlogForm(f => ({ ...f, slug: e.target.value }))}
                  className="neon-input" placeholder="post-url-slug" />
              </div>
              <div>
                <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">EXCERPT</label>
                <textarea value={blogForm.excerpt} onChange={e => setBlogForm(f => ({ ...f, excerpt: e.target.value }))}
                  className="neon-input resize-none" rows={2} placeholder="Brief description..." />
              </div>
              <div>
                <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">CONTENT (HTML)</label>
                <textarea data-testid="blog-form-content" value={blogForm.content} onChange={e => setBlogForm(f => ({ ...f, content: e.target.value }))}
                  className="neon-input resize-none font-mono text-xs" rows={8} placeholder="<h2>Heading</h2><p>Content...</p>" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">AUTHOR</label>
                  <input value={blogForm.author} onChange={e => setBlogForm(f => ({ ...f, author: e.target.value }))} className="neon-input" />
                </div>
                <div>
                  <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">TAGS (comma-separated)</label>
                  <input value={blogForm.tags} onChange={e => setBlogForm(f => ({ ...f, tags: e.target.value }))}
                    className="neon-input" placeholder="VPS, Hosting, Tips" />
                </div>
              </div>
              <div>
                <label className="font-rajdhani font-600 text-xs text-gray-500 tracking-wider block mb-1.5">COVER IMAGE URL</label>
                <input value={blogForm.cover_image} onChange={e => setBlogForm(f => ({ ...f, cover_image: e.target.value }))}
                  className="neon-input" placeholder="https://..." />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={blogForm.published} onChange={e => setBlogForm(f => ({ ...f, published: e.target.checked }))} className="w-4 h-4" />
                <span className="font-rajdhani font-600 text-sm text-gray-400">Published (visible on /blog)</span>
              </label>
            </div>
            <div className="flex items-center justify-end gap-3 p-5 border-t" style={{ borderColor: "rgba(188,19,254,0.08)" }}>
              <button onClick={() => setShowBlogForm(false)} className="btn-neon px-5 py-2 text-sm">Cancel</button>
              <button data-testid="save-blog-btn" onClick={async () => {
                setSaving(true);
                try {
                  const data = { ...blogForm, tags: blogForm.tags ? blogForm.tags.split(",").map(t => t.trim()).filter(Boolean) : [] };
                  if (editBlog) {
                    await axios.put(`${API}/blog/${editBlog.id}`, data, { headers: getAuthHeader() });
                    toast.success("Post updated!");
                  } else {
                    await axios.post(`${API}/blog`, data, { headers: getAuthHeader() });
                    toast.success("Post created!");
                  }
                  setShowBlogForm(false); fetchBlogs();
                } catch { toast.error("Failed to save post."); } finally { setSaving(false); }
              }} disabled={saving} className="btn-primary flex items-center gap-2 px-5 py-2 text-sm font-700">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {editBlog ? "UPDATE POST" : "PUBLISH POST"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
